import { React, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { format, parse } from 'date-fns'
import { Box, Typography, Skeleton } from '@mui/material'
import EntitlementCircularProgress from './entitlementCircularProgress'
import AdxStatusBar from 'components/reusable/statusBar/AdxStatusBar'
import AdxButton from 'components/custom/AdxButton'

const BalanceTypeWith = ({ backgroundLocation, item }) => {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    setTimeout(() => {
      setIsLoading(false)
    }, 1000)
  }, [])

  const applyLeave = () => {
    navigate('/app/leave/request/create', {
      state: {
        backgroundLocation: backgroundLocation,
        selectedLeave: item,
      },
    })
  }

  const grandEntitlementAmount =
    item?.entitlement?.amount + (item?.carry_forward ? item?.carry_forward?.amount : 0)

  const grandEntitlementUsed = item?.entitlement?.utilised + item?.entitlement?.outstanding

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 'calc(100vh - 35px - 70px - 50px)',
      }}
    >
      <Box
        sx={{
          py: 2,
          px: { xs: 1.5, lg: 6.25 },
          flex: 1,
        }}
      >
        <Box
          sx={{
            py: 2,
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            alignItems: 'center',
            columnGap: { xs: 0.5, lg: 5 },
            rowGap: { xs: 3, lg: 0 },
          }}
        >
          {isLoading ? (
            <Box>
              <Skeleton variant="circular" width={120} height={120} />
            </Box>
          ) : (
            <>
              <EntitlementCircularProgress
                balance={grandEntitlementAmount - grandEntitlementUsed}
                total={grandEntitlementAmount}
              />
            </>
          )}

          <Box width={3 / 4} display="flex" flexDirection="column" rowGap={1}>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: { xs: 'column', lg: 'row' },
                alignItems: 'center',
                px: 1.5,
                justifyContent: 'space-between',
              }}
            >
              <Typography variant="body1" color="light-gray-text.main">
                Entitlement
              </Typography>
              {isLoading ? (
                <Box width={'50%'}>
                  <Skeleton />
                </Box>
              ) : (
                <Typography variant="body1" color="dark-text.main">
                  {Number(grandEntitlementAmount) === 1
                    ? `${Number(grandEntitlementAmount).toFixed(1)} day`
                    : `${Number(grandEntitlementAmount).toFixed(1)} days`}
                </Typography>
              )}
            </Box>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: { xs: 'column', lg: 'row' },
                px: 1.5,
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant="body1" color="light-gray-text.main">
                Credit utilized
              </Typography>
              {isLoading ? (
                <Box width={'50%'}>
                  <Skeleton />
                </Box>
              ) : (
                <Typography variant="body1" color="dark-text.main">
                  {Number(grandEntitlementUsed) === 1
                    ? `${Number(grandEntitlementUsed).toFixed(1)} day`
                    : `${Number(grandEntitlementUsed).toFixed(1)} days`}
                </Typography>
              )}
            </Box>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: { xs: 'column', lg: 'row' },
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: 'gray-background.main',
                borderRadius: 1.1,
                py: 1.25,
                px: 1.5,
              }}
            >
              <Typography variant="body1" color="dark-text.main">
                Effective balance
              </Typography>
              {isLoading ? (
                <Box width={'50%'}>
                  <Skeleton />
                </Box>
              ) : (
                <Typography variant="body1" color="alternate.main" sx={{ fontWeight: 'bolder' }}>
                  {Number(grandEntitlementAmount - grandEntitlementUsed) === 1
                    ? `${Number(grandEntitlementAmount - grandEntitlementUsed).toFixed(1)} day`
                    : `${Number(grandEntitlementAmount - grandEntitlementUsed).toFixed(1)} days`}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            rowGap: 1,
          }}
        >
          <Box
            sx={{
              backgroundColor: 'gray-background.main',
              width: '100%',
              py: 0.5,
              px: 1.75,
              my: 2,
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" color="light-gray-text.main">
              Entitlement
            </Typography>
          </Box>

          {isLoading ? (
            <Box width={'100%'}>
              <Skeleton variant="rectangle" height={60} />
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  width: '100%',
                  px: 1.75,
                  display: 'flex',
                  flexDirection: { xs: 'column', lg: 'row' },
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography variant="body1" color="light-gray-text.main">
                  Current cycle entitlement
                </Typography>

                <Typography variant="body1" color="dark-text.main">
                  {Number(item?.entitlement?.amount) === 1
                    ? `${Number(item?.entitlement?.amount).toFixed(1)} day`
                    : `${Number(item?.entitlement?.amount).toFixed(1)} days`}
                </Typography>
              </Box>
              {item?.carry_forward && (
                <Box
                  sx={{
                    width: '100%',
                    px: 1.75,
                    display: 'flex',
                    flexDirection: { xs: 'column', lg: 'row' },
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography variant="body1" color="light-gray-text.main">
                    Carry forward (from previous cycle)
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1 }}>
                    <AdxStatusBar
                      status={{
                        text: item?.carry_forward?.expiry_date
                          ? `expired on ${format(
                              parse(item?.carry_forward?.expiry_date, 'yyyy-MM-dd', new Date()),
                              'dd MMM yyyy'
                            )}`
                          : `No Expiry`,
                        bgColor: 'light-yellow-01.main',
                        color: 'yellow-02.main',
                      }}
                    />
                    <Typography variant="body1" color="dark-text.main">
                      {item?.carry_forward
                        ? `${item?.carry_forward?.balance.toFixed(1)} ${
                            item?.carry_forward?.balance === 1 ? 'day' : 'days'
                          }`
                        : 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              )}
            </>
          )}
        </Box>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            rowGap: 1,
          }}
        >
          <Box
            sx={{
              backgroundColor: 'gray-background.main',
              width: '100%',
              py: 0.5,
              px: 1.75,
              my: 2,
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" color="light-gray-text.main">
              Credit utilized
            </Typography>
          </Box>
          {isLoading ? (
            <Box width={'100%'}>
              <Skeleton variant="rectangle" height={80} />
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  width: '100%',
                  px: 1.75,
                  display: 'flex',
                  flexDirection: { xs: 'column', lg: 'row' },
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography variant="body1" color="light-gray-text.main">
                  Utilized
                </Typography>

                <Typography variant="body1" color="dark-text.main">
                  {Number(item?.entitlement?.utilised) === 1 ||
                  Number(item?.entitlement?.utilised) === 0
                    ? `${Number(item?.entitlement?.utilised).toFixed(1)} day`
                    : `${Number(item?.entitlement?.utilised).toFixed(1)} days`}
                </Typography>
              </Box>
              <Box
                sx={{
                  width: '100%',
                  px: 1.75,
                  display: 'flex',
                  flexDirection: { xs: 'column', lg: 'row' },
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography variant="body1" color="light-gray-text.main">
                  Outstanding
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1 }}>
                  <AdxStatusBar
                    status={{
                      text: 'Revocable',
                      bgColor: 'light-yellow-01.main',
                      color: 'yellow-02.main',
                    }}
                  />
                  <Typography variant="body1" color="dark-text.main">
                    {Number(item?.entitlement?.outstanding) === 1 ||
                    Number(item?.entitlement?.outstanding) === 0
                      ? `${Number(item?.entitlement?.outstanding).toFixed(1)} day`
                      : `${Number(item?.entitlement?.outstanding).toFixed(1)} days`}
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Box>
      <Box sx={{ px: { xs: 1.5, lg: 6.25 }, my: 2, width: '100%' }}>
        <AdxButton onClick={applyLeave} fullWidth>
          Apply Leave
        </AdxButton>
      </Box>
    </Box>
  )
}

export default BalanceTypeWith
